import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Enhanced lazy loading with preload capability
 * This allows us to preload components before they're needed
 */
export interface PreloadableComponent<T extends ComponentType<any>> extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

export function lazyWithPreload<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  const LazyComponent = lazy(importFn) as PreloadableComponent<T>;
  LazyComponent.preload = importFn;
  return LazyComponent;
}

/**
 * Preload components based on route or user action
 */
export const preloadComponent = (component: PreloadableComponent<any>) => {
  component.preload();
};

/**
 * Preload multiple components
 */
export const preloadComponents = (components: PreloadableComponent<any>[]) => {
  components.forEach(component => component.preload());
};

/**
 * Route-based preloading strategy
 */
export const routePreloadMap: Record<string, () => void> = {
  '/': () => {
    // Preload common components that might be needed after homepage
    // This is called when user lands on homepage
  },
  '/editor': () => {
    // Preload editor-specific components
  },
  '/dashboard': () => {
    // Preload dashboard components
  },
  '/learn': () => {
    // Preload learning components
  }
};

/**
 * Preload components based on current route
 */
export const preloadForRoute = (pathname: string) => {
  const preloader = routePreloadMap[pathname];
  if (preloader) {
    preloader();
  }
};

/**
 * Intersection Observer based preloading
 * Preload components when a trigger element comes into view
 */
export const setupIntersectionPreload = (
  triggerElement: Element,
  component: PreloadableComponent<any>
) => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          component.preload();
          observer.disconnect();
        }
      });
    },
    { rootMargin: '50px' } // Start loading 50px before element is visible
  );

  observer.observe(triggerElement);
  return observer;
};

/**
 * Idle time preloading
 * Preload components when the browser is idle
 */
export const preloadOnIdle = (component: PreloadableComponent<any>) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      component.preload();
    });
  } else {
    // Fallback for browsers that don't support requestIdleCallback
    setTimeout(() => {
      component.preload();
    }, 1);
  }
};